import { useRef, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Panel, DefaultButton } from "@fluentui/react";
import readNDJSONStream from "ndjson-readablestream";
import Footer from "../../components/Footer/Footer";
import { Checkbox } from "@fluentui/react-components";
import { VaeEmailButton } from "../../components/VaeEmailButton/VaeEmailButton";

import styles from "./Chat.module.css";

import { chatApi, configApi, ChatAppResponse, ChatAppResponseOrError, ChatAppRequest, ResponseMessage } from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { EmailComposer } from "../../components/EmailComposer/EmailComposer";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { HistoryPanel } from "../../components/HistoryPanel";
import { HistoryProviderOptions, useHistoryManager } from "../../components/HistoryProviders";
import { HistoryButton } from "../../components/HistoryButton";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { useLogin, getToken, requireAccessControl } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { LoginContext } from "../../loginContext";
import { Settings } from "../../components/Settings/Settings";

// Define a new type for the user message
export interface UserMessage {
    subject: string;
    body: string;
}

// Helper functions for serializing/deserializing UserMessage
const serializeUserMessage = (msg: UserMessage): string => JSON.stringify(msg);
const deserializeUserMessage = (s: string): UserMessage => JSON.parse(s);

const Chat = () => {
    // Configuration and settings states
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
    const [temperature, setTemperature] = useState<number>(0.3);
    const [seed, setSeed] = useState<number | null>(null);
    const [vae, setVae] = useState<boolean>(false);
    const [reasoningEffort, setReasoningEffort] = useState<string>("");
    const [streamingEnabled, setStreamingEnabled] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(false);

    // Replace the string-based reference with one that holds a UserMessage.
    const lastMessageRef = useRef<UserMessage | null>(null);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);
    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    // Update answers to store a UserMessage (for user) paired with a ChatAppResponse.
    const [answers, setAnswers] = useState<[user: UserMessage, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: UserMessage, response: ChatAppResponse][]>([]);

    const [showReasoningEffortOption, setShowReasoningEffortOption] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryBlob, setShowChatHistoryBlob] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);

    const { t, i18n } = useTranslation();

    const getConfig = async () => {
        configApi().then(config => {
            setShowReasoningEffortOption(config.showReasoningEffortOption);
            setStreamingEnabled(config.streamingEnabled);
            if (!config.streamingEnabled) {
                setShouldStream(false);
            }
            if (config.showReasoningEffortOption) {
                setReasoningEffort(config.defaultReasoningEffort);
            }
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryBlob(config.showChatHistoryBlob);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
        });
    };

    // Update the async streaming handler to accept a UserMessage.
    const handleAsyncRequest = async (userMsg: UserMessage, answersArr: [UserMessage, ChatAppResponse][], responseBody: ReadableStream<any>) => {
        let responseContent: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    responseContent += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        message: { content: responseContent, role: askResponse.message.role }
                    };
                    setStreamedAnswers([...answersArr, [userMsg, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };        
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                // Always capture session_state from any chunk that includes it
                if (event["session_state"] !== undefined) {
                    askResponse = { ...askResponse, session_state: event["session_state"] } as ChatAppResponse;
                }
                if (event["context"]) {
                    // Handle context updates - this includes thoughts, metadata, etc.
                    if (event["delta"] && event["delta"]["role"]) {
                        event["message"] = event["delta"];
                        askResponse = { ...askResponse, ...event } as ChatAppResponse;
                    } else {
                        askResponse.context = { ...askResponse.context, ...event["context"] };
                    }
                } else if (event["delta"] && event["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["delta"]["content"]);
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: { content: responseContent, role: askResponse.message.role }
        };
        return fullResponse;
    };

    const client = useLogin ? useMsal().instance : undefined;
    let userEmail = "";
    if (client) {
        const account = client.getActiveAccount() || client.getAllAccounts()[0];
        userEmail = account?.username || "";
    }
    const allowedDevEmails = ["ramandeep.s.dehal@gsk.com", "matthew.8.mason@gsk.com", "daniel.d.sinia@gsk.com"];
    const isDevUser = allowedDevEmails.includes(userEmail);

    const { loggedIn } = useContext(LoginContext);

    const historyProvider: HistoryProviderOptions = (() => {
        if (useLogin && showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (useLogin && showChatHistoryBlob) return HistoryProviderOptions.BlobStorage;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();    
    const historyManager = useHistoryManager(historyProvider);

    /**
     * Fire-and-forget helper to save a single compliance check to chat history.
     * Runs fully async so it never blocks the UI or other API calls.
     */
    const saveToHistory = (userMsg: UserMessage, response: ChatAppResponse) => {
        if (typeof response.session_state !== "string" || response.session_state === "") {
            console.warn("Chat history not saved: session_state is missing or not a string. Value:", response.session_state);
            return;
        }
        // Use an immediately-invoked async function so we don't block the caller
        (async () => {
            try {
                const token = client ? await getToken(client) : undefined;
                await historyManager.addItem(
                    response.session_state,
                    [[serializeUserMessage(userMsg), response]] as [string, ChatAppResponse][],
                    token
                );
            } catch (historyError) {
                console.error("Failed to save chat history:", historyError);
            }
        })();
    };

    // Update makeApiRequest to receive a UserMessage rather than a string.
    const makeApiRequest = async (userMsg: UserMessage) => {
        lastMessageRef.current = userMsg;
        error && setError(undefined);
        setIsLoading(true);
        setActiveAnalysisPanelTab(undefined);

        // Clear previous answers so each request overwrites the previous response
        setAnswers([]);
        setStreamedAnswers([]);

        const token = client ? await getToken(client) : undefined;

        try {
            // Build the history of messages.
            // For user messages, we now send an object in the “content” field.
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: { subject: a[0].subject, body: a[0].body }, role: "user" },
                // For assistant messages, we assume the subject is empty.
                { content: a[1].message.content, role: "assistant" }
            ]);
            // Add the current user message.
            messages.push({ content: { subject: userMsg.subject, body: userMsg.body }, role: "user" });
            const request: ChatAppRequest = {
                messages: messages,
                context: {
                    overrides: {
                        temperature: temperature,
                        reasoning_effort: reasoningEffort,
                        vae_enabled: vae,
                        language: i18n.language,
                        ...(seed !== null ? { seed: seed } : {})
                    }
                },
                // Always null so the backend generates a new session ID per request.
                // Each email analysis is independent — reusing a session_state would
                // overwrite the previous chat history entry in blob storage.
                session_state: null
            };

            const response = await chatApi(request, shouldStream, token);
            if (!response.body) {
                throw Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                throw Error(`Request failed with status ${response.status}`);
            }            
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(userMsg, answers, response.body);
                setAnswers([[userMsg, parsedResponse]]);
                // Fire-and-forget: save to history asynchronously without blocking UI
                saveToHistory(userMsg, parsedResponse);
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (parsedResponse.error) {
                    throw Error(parsedResponse.error);
                }
                setAnswers([[userMsg, parsedResponse as ChatAppResponse]]);
                // Fire-and-forget: save to history asynchronously without blocking UI
                saveToHistory(userMsg, parsedResponse as ChatAppResponse);
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };
    const clearChat = () => {
        // Clear only conversation results, not the email input fields
        error && setError(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        // Keep lastMessageRef to maintain email fields
    };

    const clearAll = () => {
        // Clear everything: email fields AND conversation results
        lastMessageRef.current = null;
        error && setError(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    const handleSettingsChange = (field: string, value: any) => {
        switch (field) {
            case "temperature":
                setTemperature(value);
                break;
            case "seed":
                setSeed(value);
                break;
            case "vaeEnabled":
                setVae(value);
                break;
            case "shouldStream":
                setShouldStream(value);
                break;
        }
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }
        setSelectedAnswer(index);
    };

    if (useLogin && !loggedIn) {
        // If login is required and user is not logged in, show only a minimal placeholder (header is always rendered by Layout)
        return (
            <div className={styles.container}>
                <Helmet>
                    <title>{t("pageTitle")}</title>
                </Helmet>
                <div style={{ textAlign: "center", margin: "2rem", fontSize: "1.25rem", color: "#555" }}>
                    {t("loginToViewApp", "Please login to view the application!")}
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{t("pageTitle")}</title>
            </Helmet>
            <h2 className={styles.chatEmptyStateSubtitle}>{t("chatEmptyStateSubtitle")}</h2>
            <div className={styles.commandsSplitContainer}>
                <div className={styles.commandsContainer}>
                    {((useLogin && showChatHistoryCosmos) || (useLogin && showChatHistoryBlob) || showChatHistoryBrowser) && (
                        <HistoryButton className={styles.commandButton} onClick={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)} />
                    )}
                </div>
                <div className={styles.commandsContainer}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <ClearChatButton className={styles.commandButton} onClick={clearChat} canClear={!!lastMessageRef.current && !isLoading} />
                        <VaeEmailButton className={styles.commandButton} vaeEnabled={vae} onClick={() => setVae(v => !v)} />
                        {isDevUser && <SettingsButton className={styles.commandButton} onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)} />}
                    </div>
                </div>
            </div>
            <div className={styles.chatRoot} style={{ marginLeft: isHistoryPanelOpen ? "300px" : "0" }}>
                <div className={styles.chatContainer}>
                    {" "}
                    <div className={styles.chatInput}>
                        <EmailComposer
                            disabled={isLoading}
                            vaeEnabled={vae}
                            hasAnswers={answers.length > 0}
                            onSend={(subject: string, body: string) => makeApiRequest({ subject, body })}
                            onClearAll={clearAll}
                        />
                    </div>
                    {!lastMessageRef.current ? (
                        <div className={styles.chatEmptyState}></div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index} className={styles.messageGroup}>
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                index={index}
                                                isSelected={false}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {!isStreaming &&
                                answers.map((answerPair, index) => (
                                    <div key={index} className={styles.messageGroup}>
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={false}
                                                key={index}
                                                answer={answerPair[1]}
                                                index={index}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {isLoading && (
                                <div className={styles.messageGroup}>
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </div>
                            )}
                            {error ? (
                                <div className={styles.messageGroup}>
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError
                                            error={error.toString()}
                                            onRetry={() => lastMessageRef.current && makeApiRequest(lastMessageRef.current)}
                                        />
                                    </div>
                                </div>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}
                </div>{" "}
                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}
                {((useLogin && showChatHistoryCosmos) || (useLogin && showChatHistoryBlob) || showChatHistoryBrowser) && (
                    <HistoryPanel
                        provider={historyProvider}
                        isOpen={isHistoryPanelOpen}
                        notify={!isStreaming && !isLoading}
                        onClose={() => setIsHistoryPanelOpen(false)}
                        onChatSelected={(histAnswers: [string, ChatAppResponse][]) => {
                            if (histAnswers.length === 0) return;
                            const converted = histAnswers.map(([s, resp]) => [deserializeUserMessage(s), resp]) as [UserMessage, ChatAppResponse][];
                            setAnswers(converted);
                            lastMessageRef.current = converted[converted.length - 1][0];
                        }}
                    />
                )}
                <Panel
                    headerText={t("labels.headerText")}
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel={t("labels.closeButton")}
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>{t("labels.closeButton")}</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <Settings
                        temperature={temperature}
                        seed={seed}
                        reasoningEffort={reasoningEffort}
                        showReasoningEffortOption={showReasoningEffortOption}
                        useLogin={!!useLogin}
                        loggedIn={loggedIn}
                        streamingEnabled={streamingEnabled}
                        shouldStream={shouldStream}
                        onChange={handleSettingsChange}
                    />
                    {useLogin && <TokenClaimsDisplay />}
                </Panel>
            </div>
            <Footer />
        </div>
    );
};

export default Chat;
